import {
  BBox,
  Vec2,
  angleInArc,
  clamp,
  entityBBox,
  entityCenters,
  entityEndpoints,
  entityMidpoints,
  entityQuadrants,
  intersectEntities,
  type Entity,
  type EntityId,
} from '@lakaran/geometry';
import type { CadDocument } from './document';

export type SnapType =
  | 'endpoint'
  | 'intersection'
  | 'midpoint'
  | 'center'
  | 'quadrant'
  | 'perpendicular'
  | 'tangent'
  | 'grid';

export const SNAP_ORDER: readonly SnapType[] = [
  'endpoint',
  'intersection',
  'midpoint',
  'center',
  'quadrant',
  'perpendicular',
  'tangent',
  'grid',
];

export interface SnapSettings {
  enabled: Set<SnapType>;
  gridSize: number;
}

export function defaultSnapSettings(): SnapSettings {
  return {
    enabled: new Set<SnapType>([
      'endpoint',
      'intersection',
      'midpoint',
      'center',
      'quadrant',
      'perpendicular',
      'tangent',
      'grid',
    ]),
    gridSize: 10,
  };
}

export interface SnapCandidate {
  point: Vec2;
  type: SnapType;
  entityIds: EntityId[];
  /** World-space distance from the query point, in millimetres. */
  distance: number;
}

const MAX_INTERSECTION_ENTITIES = 12;

/**
 * Object snap engine. Pure geometry: the caller supplies the search aperture in
 * world units (typically `aperturePx / zoom`), so no view state leaks in here.
 */
export class SnapEngine {
  settings: SnapSettings;

  constructor(settings: SnapSettings = defaultSnapSettings()) {
    this.settings = settings;
  }

  /** All candidates within the aperture, ordered by priority then distance. */
  compute(
    doc: CadDocument,
    cursor: Vec2,
    radius: number,
    reference?: Vec2,
  ): SnapCandidate[] {
    const searchBox = BBox.around(cursor, radius * 1.5);
    const nearby = doc.queryVisible(searchBox);
    const candidates: SnapCandidate[] = [];

    const add = (point: Vec2, type: SnapType, entityIds: EntityId[] = []): void => {
      const distance = point.distanceTo(cursor);
      if (distance > radius) return;
      candidates.push({ point, type, entityIds, distance });
    };

    const enabled = this.settings.enabled;

    if (enabled.has('endpoint')) {
      for (const entity of nearby) {
        for (const point of entityEndpoints(entity)) add(point, 'endpoint', [entity.id]);
      }
    }

    if (enabled.has('midpoint')) {
      for (const entity of nearby) {
        for (const point of entityMidpoints(entity)) add(point, 'midpoint', [entity.id]);
      }
    }

    if (enabled.has('center')) {
      for (const entity of nearby) {
        for (const point of entityCenters(entity)) add(point, 'center', [entity.id]);
      }
    }

    // A circle's four quadrant points: the five grips (centre + quadrants) it offers by default.
    if (enabled.has('quadrant')) {
      for (const entity of nearby) {
        for (const point of entityQuadrants(entity)) add(point, 'quadrant', [entity.id]);
      }
    }

    if (enabled.has('intersection')) {
      const nearest = this.nearestEntities(nearby, cursor, MAX_INTERSECTION_ENTITIES);
      for (let i = 0; i < nearest.length; i++) {
        for (let j = i + 1; j < nearest.length; j++) {
          const a = nearest[i]!;
          const b = nearest[j]!;
          for (const point of intersectEntities(a, b, { chordTolerance: 0.01 })) {
            add(point, 'intersection', [a.id, b.id]);
          }
        }
      }
    }

    if (reference) {
      if (enabled.has('perpendicular')) {
        for (const entity of nearby) {
          for (const point of perpendicularPoints(entity, reference)) {
            add(point, 'perpendicular', [entity.id]);
          }
        }
      }
      if (enabled.has('tangent')) {
        for (const entity of nearby) {
          for (const point of tangentPoints(entity, reference)) {
            add(point, 'tangent', [entity.id]);
          }
        }
      }
    }

    if (enabled.has('grid')) {
      const size = Math.max(this.settings.gridSize, 1e-6);
      const gridPoint = Vec2.of(
        Math.round(cursor.x / size) * size,
        Math.round(cursor.y / size) * size,
      );
      add(gridPoint, 'grid');
    }

    const priority = new Map<SnapType, number>(SNAP_ORDER.map((type, index) => [type, index]));
    candidates.sort((a, b) => {
      const pa = priority.get(a.type) ?? 99;
      const pb = priority.get(b.type) ?? 99;
      if (pa !== pb) return pa - pb;
      return a.distance - b.distance;
    });
    return candidates;
  }

  best(
    doc: CadDocument,
    cursor: Vec2,
    radius: number,
    reference?: Vec2,
  ): SnapCandidate | null {
    return this.compute(doc, cursor, radius, reference)[0] ?? null;
  }

  private nearestEntities(
    entities: Entity[],
    cursor: Vec2,
    limit: number,
  ): Entity[] {
    const scored = entities
      .map((entity) => ({ entity, distance: entityBBox(entity).center.distanceTo(cursor) }))
      .sort((a, b) => a.distance - b.distance)
      .slice(0, limit);
    return scored.map((s) => s.entity);
  }
}

function perpendicularPoints(entity: Entity, reference: Vec2): Vec2[] {
  const out: Vec2[] = [];
  if (entity.type === 'line') {
    const foot = footOnSegment(entity.a, entity.b, reference);
    if (foot) out.push(foot);
  } else if (entity.type === 'polyline') {
    const v = entity.vertices;
    for (let i = 1; i < v.length; i++) {
      const foot = footOnSegment(v[i - 1]!, v[i]!, reference);
      if (foot) out.push(foot);
    }
    if (entity.closed && v.length > 2) {
      const foot = footOnSegment(v[v.length - 1]!, v[0]!, reference);
      if (foot) out.push(foot);
    }
  } else if (entity.type === 'circle' || entity.type === 'arc') {
    const dir = reference.sub(entity.center);
    if (dir.isZero()) return out;
    const angle = dir.angle();
    if (entity.type === 'arc' && !angleInArc(angle, entity.startAngle, entity.endAngle, entity.ccw)) {
      return out;
    }
    out.push(entity.center.add(dir.normalize().scale(entity.radius)));
  }
  return out;
}

function footOnSegment(a: Vec2, b: Vec2, p: Vec2): Vec2 | null {
  const ab = b.sub(a);
  const lenSq = ab.lenSq();
  if (lenSq < 1e-18) return null;
  const t = p.sub(a).dot(ab) / lenSq;
  if (t < 0 || t > 1) return null;
  return a.add(ab.scale(t));
}

function tangentPoints(entity: Entity, from: Vec2): Vec2[] {
  if (entity.type !== 'circle' && entity.type !== 'arc') return [];
  const center = entity.center;
  const radius = entity.radius;
  const d = from.distanceTo(center);
  if (d <= radius + 1e-9) return [];

  const baseAngle = from.sub(center).angle();
  const offset = Math.acos(clamp(radius / d, -1, 1));
  const out: Vec2[] = [];
  for (const angle of [baseAngle + offset, baseAngle - offset]) {
    if (entity.type === 'arc' && !angleInArc(angle, entity.startAngle, entity.endAngle, entity.ccw)) {
      continue;
    }
    out.push(center.add(Vec2.polar(radius, angle)));
  }
  return out;
}
